'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React from 'react';

import BuyGiftCardsForm from '@/app/[locale]/(default-layout)/(forms)/_components/BuyGiftCardsForm';
import { getFormContact } from '@/app/[locale]/(default-layout)/(forms)/_components/form-contact-helper';
import {
    resolveFormPageText,
    resolveFormPageTitle,
    resolveFormImageUrl
} from '@/app/[locale]/(default-layout)/(forms)/_components/form-image-helper';
import FormContactLinks from '@/app/[locale]/(default-layout)/(forms)/_components/FormContactLinks';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { ID_KINO_SOLOTHURN } from '@cineamo/api/constants/cinema-ids.ts';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { datePlus, isDateFuture } from '@cineamo/ui/src/helpers/date-helper';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import { IconDown16 } from '@cineamo/ui/src/icons/ic-down.tsx';
import { IconExternalLink16 } from '@cineamo/ui/src/icons/ic-externalLink.tsx';
import CinemaDescriptionMarkdown from '@cineamo/ui/src/markdown/CinemaDescriptionMarkdown';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import GiftCardTypeCard, { GiftCardTypeCardButton } from './GiftCardTypeCard';

type BuyGiftCardsPageClientProps = {
    domainConfiguration: DomainConfigurationDto;
    /** Fallback hero image (first cinema room picture), used when no image is configured. */
    hallImageUrl?: string;
};

const BuyGiftCardsPageClient: React.FC<BuyGiftCardsPageClientProps> = ({ domainConfiguration, hallImageUrl }) => {
    const t = useTranslations();
    const isMounted = useIsMounted();

    const { phone, phoneURI, email } = getFormContact(domainConfiguration, 'giftCards');

    const heroImage = resolveFormImageUrl(domainConfiguration, 'giftCards', hallImageUrl);
    const customDescription = resolveFormPageText(domainConfiguration, 'giftCards');
    const customPageTitle = resolveFormPageTitle(domainConfiguration, 'giftCards');

    const showPrintAtHome = domainConfiguration.cinema.id === ID_KINO_SOLOTHURN;

    const printAtHomeLaunchDate = new Date('2026-05-08');
    const isPrintAtHomeNew = isDateFuture(datePlus(printAtHomeLaunchDate, 30));

    const scrollToOrderForm = () => {
        document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const orderFormButton: GiftCardTypeCardButton = {
        title: t('buy-gift-cards.types.order-form-button'),
        onClick: scrollToOrderForm,
        trailingIcon: <IconDown16 />
    };

    type GiftCardType = {
        icon?: string;
        title?: string;
        description?: string;
        button?: GiftCardTypeCardButton;
        isNew?: boolean;
    };

    const giftCardTypes: (GiftCardType | undefined)[] = [
        showPrintAtHome
            ? {
                  icon: '🎟️',
                  title: t('buy-gift-cards.types.print-at-home'),
                  description: t('buy-gift-cards.types.print-at-home-description'),
                  button: {
                      title: t('buy-gift-cards.types.print-at-home-button'),
                      href: 'https://ticketonline.kinosolothurn.ch/Cinesol/VoucherShop',
                      trailingIcon: <IconExternalLink16 />
                  },
                  isNew: isPrintAtHomeNew
              }
            : undefined,
        {
            icon: '🎁',
            title: t('buy-gift-cards.types.regular'),
            description: t('buy-gift-cards.types.regular-description'),
            button: orderFormButton
        },
        {
            icon: '🍿',
            title: t('buy-gift-cards.types.concessions'),
            description: t('buy-gift-cards.types.concessions-description'),
            button: orderFormButton
        },
        {
            icon: '🎬',
            title: t('buy-gift-cards.types.premium'),
            description: t('buy-gift-cards.types.premium-description'),
            button: orderFormButton
        }
    ];
    const visibleGiftCardTypes = giftCardTypes.filter((giftCardType) => giftCardType !== undefined);

    return (
        <main className="min-h-screen">
            {/* Hero Section */}
            <div className="py-[96px] space-y-[48px]">
                <section>
                    <CenterLayout>
                        <div className="text-center space-y-8">
                            <div className="space-y-4">
                                <h1
                                    className="text-headline-24 md:text-headline-32"
                                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                    {customPageTitle || t('buy-gift-cards.page-title')}
                                </h1>
                                {!customDescription && (
                                    <h2
                                        className="text-body-16 md:text-body-20"
                                        style={{ color: domainConfiguration.style.primaryColor }}>
                                        {t('buy-gift-cards.hero.subtitle')}
                                    </h2>
                                )}
                                {customDescription && (
                                    <div
                                        className="text-body-16 max-w-3xl mx-auto"
                                        style={{
                                            color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8)
                                        }}>
                                        <CinemaDescriptionMarkdown
                                            description={customDescription}
                                            allowInlineHtml
                                        />
                                    </div>
                                )}
                            </div>
                            {heroImage && isMounted && (
                                <div className="relative w-full max-w-3xl mx-auto aspect-[16/9] rounded-12 overflow-hidden">
                                    <Image
                                        src={heroImage}
                                        alt={t('buy-gift-cards.page-title')}
                                        fill
                                        className="object-cover"
                                        sizes="(max-width: 768px) 100vw, 768px"
                                    />
                                </div>
                            )}
                        </div>
                    </CenterLayout>
                </section>

                {/* The gift card types are default copy, so a custom page text replaces them. */}
                {!customDescription && (
                    <section>
                        <CenterLayout>
                            <div className="max-w-6xl mx-auto">
                                <h2
                                    className="text-headline-32 text-center mb-16"
                                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                    {t('buy-gift-cards.types.title')}
                                </h2>
                                <div className="flex flex-wrap justify-center gap-16">
                                    {visibleGiftCardTypes.map((giftCardType) => (
                                        <GiftCardTypeCard
                                            key={giftCardType.title}
                                            icon={giftCardType.icon}
                                            title={giftCardType.title}
                                            description={giftCardType.description}
                                            button={giftCardType.button}
                                            isNew={giftCardType.isNew}
                                            className="w-full sm:w-[260px]"
                                            domainConfiguration={domainConfiguration}
                                        />
                                    ))}
                                </div>
                            </div>
                        </CenterLayout>
                    </section>
                )}
            </div>

            {/* Order Form Section */}
            <section
                id="order-form"
                className="py-32 scroll-mt-32">
                <CenterLayout>
                    <div className="max-w-2xl mx-auto">
                        <div className="text-center mb-12">
                            <h2
                                className="text-headline-32 mb-4"
                                style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                {t('buy-gift-cards.form.title')}
                            </h2>
                        </div>

                        <BuyGiftCardsForm domainConfiguration={domainConfiguration} />
                    </div>
                </CenterLayout>
            </section>

            {/* Info Section */}
            <section
                className="py-32 backdrop-blur-sm"
                style={{ backgroundColor: withOpacity(domainConfiguration.style.onBackgroundColor, 0.05) }}>
                <CenterLayout>
                    <div className="text-center space-y-8">
                        {/* Shipping and payment notes are default copy too. A custom page text replaces them,
                            so an operator who overrides the page owns these terms — the contact links below
                            stay, since they carry the cinema's real phone number and address. */}
                        {!customDescription && (
                            <>
                                <h2
                                    className="text-headline-32"
                                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                                    {t('buy-gift-cards.info.title')}
                                </h2>
                                <div className="max-w-3xl mx-auto space-y-4">
                                    <p
                                        className="text-body-16"
                                        style={{
                                            color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8)
                                        }}>
                                        {t('buy-gift-cards.info.shipping')}
                                    </p>
                                    <p
                                        className="text-body-16"
                                        style={{
                                            color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8)
                                        }}>
                                        {t('buy-gift-cards.info.payment-terms')}
                                    </p>
                                </div>
                            </>
                        )}

                        {/* Contact Information */}
                        <div className="pt-8">
                            <FormContactLinks
                                domainConfiguration={domainConfiguration}
                                namespace="buy-gift-cards"
                                phone={phone}
                                phoneURI={phoneURI}
                                email={email}
                            />
                        </div>
                    </div>
                </CenterLayout>
            </section>
        </main>
    );
};

export default BuyGiftCardsPageClient;
